import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Routes, Route, useLocation, Link } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpIcon from '@mui/icons-material/Help';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ConversationList from './components/ConversationList';
import HelpModal from './components/HelpModal';

// Create a theme instance matching FeedTahoe's corporate style
const theme = createTheme({
  palette: {
    primary: {
      main: '#015887', // FeedTahoe standardized blue
      dark: '#014569',
      light: '#E6F3F8',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#F8971C', // FeedTahoe standardized orange
      light: '#FFA940',
      dark: '#D67B0A',
    },
    background: {
      default: '#FAFAFA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#015887', // Using FeedTahoe blue for primary text
      secondary: '#6B7280', // Gray for secondary text
    },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h6: {
      fontFamily: '"Merriweather", "Georgia", serif',
      fontWeight: 600,
      letterSpacing: '0.0075em',
    },
    subtitle1: {
      fontWeight: 500,
    },
    body1: {
      fontSize: '0.9375rem',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#2C3E50',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 6,
          fontWeight: 500,
        },
        containedSecondary: {
          color: '#FFFFFF',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 159, 60, 0.08)',
          },
        },
      },
    },
  },
});

function App() {
  const [isNewConversationOpen, setIsNewConversationOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [userName, setUserName] = useState(localStorage.getItem('userName') || '');
  const [tempUserName, setTempUserName] = useState(userName);
  const location = useLocation();

  console.log('App render - userName state:', {
    userName,
    tempUserName,
    localStorageUserName: localStorage.getItem('userName')
  });

  const handleNewConversation = () => {
    setIsNewConversationOpen(true);
  };

  const handleSettingsOpen = () => {
    console.log('Opening settings dialog with current userName:', userName);
    setTempUserName(userName);
    setIsSettingsOpen(true);
  };

  const handleSettingsSave = () => {
    console.log('Saving settings - tempUserName:', tempUserName);
    if (tempUserName.trim()) {
      setUserName(tempUserName.trim());
      localStorage.setItem('userName', tempUserName.trim());
      setIsSettingsOpen(false);
    }
  };

  const handleClearName = () => {
    console.log('Clearing username');
    setUserName('');
    setTempUserName('');
    localStorage.removeItem('userName');
    setIsSettingsOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100vh',
        backgroundColor: 'background.default'
      }}>
        <AppBar 
          position="fixed" 
          elevation={0}
          sx={{ 
            backgroundColor: 'white',
            color: 'primary.main',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            borderBottom: '1px solid',
            borderColor: 'rgba(1, 88, 135, 0.1)',
          }}
        >
          <Container maxWidth={false} disableGutters sx={{ maxWidth: '1920px !important' }}>
            <Toolbar sx={{ justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                  <img 
                    src="/logo.png" 
                    alt="FeedTahoe Logo" 
                    style={{ 
                      height: 50, 
                      marginRight: 8,
                      filter: 'brightness(0) saturate(100%) invert(22%) sepia(88%) saturate(1960%) hue-rotate(187deg) brightness(94%) contrast(101%)'
                    }} 
                  />
                  <Typography variant="h6" component="div" sx={{ 
                    color: 'primary.main',
                    fontWeight: 600,
                    '&:hover': { 
                      opacity: 0.85
                    }
                  }}>
                    <span style={{ color: '#F8971C' }}>Feed</span>Tahoe
                  </Typography>
                </Link>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  onClick={handleNewConversation}
                  sx={{ 
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    width: 40,
                    height: 40,
                    boxShadow: '0 2px 4px rgba(1, 88, 135, 0.2)',
                  }}
                >
                  <AddIcon />
                </IconButton>
                <IconButton 
                  onClick={handleSettingsOpen}
                  sx={{ 
                    backgroundColor: 'secondary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'secondary.dark',
                    },
                    width: 40,
                    height: 40,
                    boxShadow: '0 2px 4px rgba(248, 151, 28, 0.2)',
                  }}
                >
                  <SettingsIcon />
                </IconButton>
                <IconButton 
                  onClick={() => setIsHelpOpen(true)}
                  sx={{ 
                    p: 0,
                    width: 40,
                    height: 40,
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <HelpIcon sx={{ 
                    width: 40,
                    height: 40,
                    p: 0.8,
                    backgroundColor: '#E0E0E0',
                    borderRadius: 1,
                    color: '#757575',
                    '&:hover': {
                      backgroundColor: '#BDBDBD'
                    }
                  }} />
                </IconButton>
              </Box>
            </Toolbar>
          </Container>
          <Box 
            sx={{ 
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, #015887 0%, #F8971C 100%)',
              opacity: 0.8,
            }} 
          />
        </AppBar>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            height: '100vh',
            overflow: 'hidden',
            position: 'relative',
            pt: {
              xs: '57px',
              sm: '65px',
            },
          }}
        >
          <Container 
            maxWidth={false} 
            sx={{ 
              height: '100%',
              p: { xs: 0, sm: 0 },
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '1920px !important',
              mx: 'auto'
            }}
          >
            <Routes>
              <Route 
                path="/" 
                element={
                  <ConversationList 
                    isNewConversationOpen={isNewConversationOpen}
                    onNewConversationClose={() => setIsNewConversationOpen(false)}
                    userName={userName}
                    onOpenSettings={handleSettingsOpen}
                  />
                } 
              />
              <Route 
                path="/c/:phoneNumber" 
                element={
                  <ConversationList 
                    isNewConversationOpen={isNewConversationOpen}
                    onNewConversationClose={() => setIsNewConversationOpen(false)}
                    initialPhoneNumber={location.pathname.split('/c/')[1]}
                    userName={userName}
                    onOpenSettings={handleSettingsOpen}
                  />
                } 
              />
            </Routes>
          </Container>
        </Box>

        {/* Add HelpModal */}
        <HelpModal 
          open={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
        />

        {/* Settings Modal */}
        <Dialog 
          open={isSettingsOpen} 
          onClose={() => setIsSettingsOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            fontFamily: 'Merriweather, Georgia, serif',
            borderBottom: '2px solid',
            borderColor: 'secondary.main',
            pb: 2
          }}>
            Settings
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Your Name"
              variant="outlined"
              value={tempUserName}
              onChange={(e) => setTempUserName(e.target.value)}
              margin="normal"
              placeholder="Enter your name"
              helperText={
                <Box component="span" sx={{ display: 'block' }}>
                  <Typography 
                    variant="caption" 
                    component="span" 
                    sx={{ color: 'text.secondary' }}
                  >
                    This name will be associated with messages you send
                  </Typography>
                  {userName && (
                    <Typography 
                      variant="caption" 
                      component="span"
                      sx={{ color: 'text.secondary' }}
                    >
                      {' '}(
                      <Button
                        onClick={handleClearName}
                        sx={{
                          color: 'error.main',
                          p: 0,
                          minWidth: 'auto',
                          fontSize: 'inherit',
                          fontWeight: 'inherit',
                          textTransform: 'none',
                          verticalAlign: 'baseline',
                          '&:hover': {
                            background: 'none',
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        clear name
                      </Button>
                      )
                    </Typography>
                  )}
                </Box>
              }
              error={!tempUserName.trim()}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={() => setIsSettingsOpen(false)}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSettingsSave}
              variant="contained" 
              color="secondary"
              disabled={!tempUserName.trim()}
            >
              Save Settings
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}

export default App; 